/**
 * Hook para gestionar la navegación entre bloques y preguntas
 * 
 * Maneja transiciones, descarga de resultados y conversión de respuestas.
 * 
 * @param {Object} testState - Estado actual de la prueba
 * @param {Function} setTestState - Función para actualizar el estado
 * @param {Function} navigateToScreen - Función para cambiar pantallas
 * @returns {Object} Funciones de navegación
 * @author Ana Lizcano
 * @hook
 */

import { useCallback } from 'react';
import { loadBlockQuestions } from '../data/lazyQuestions';
import testService from '../../../services/testService';
import { useAuth } from '../../../context/authContext';

const useTestNavigation = (testState, setTestState, navigateToScreen) => {
  const { user } = useAuth();
  
  const navigateToBlock = useCallback((blockKey, questionIndex = 0) => {
    setTestState(prev => ({
      ...prev,
      currentBlock: blockKey,
      currentQuestion: questionIndex
    }));
  }, [setTestState]);

  const navigateToQuestion = useCallback((questionIndex) => {
    setTestState(prev => ({
      ...prev,
      currentQuestion: questionIndex
    }));
  }, [setTestState]);

  /**
   * Descarga los resultados de un bloque específico
   */
  const downloadBlockResults = useCallback(async (blockKey, candidateId) => {
    try {
      switch (blockKey) {
        case 'A':
          const blockAResponses = testState.responses.blockA;
          if (blockAResponses && blockAResponses.length > 0) {
            try {
              const respuestasFormateadas = convertBlockAForCleaver(blockAResponses);
              await testService.enviarRespuestasTestA(candidateId, respuestasFormateadas);
            } catch (error) {
            }
          }
          break;
          
        case 'B':
          const blockBResponses = testState.responses.blockB;
          if (blockBResponses && blockBResponses.length > 0) {
            try {
              const datosConvertidos = convertBlockBForTestValores(blockBResponses);
              const respuestasFormateadas = testService.formatearRespuestasTestB(datosConvertidos);
              await testService.enviarRespuestasTestB(candidateId, respuestasFormateadas);
            } catch (error) {
            }
          }
          break;
          
        case 'C':
          const blockCResponses = testState.responses.blockC;
          if (blockCResponses && blockCResponses.length > 0) {
            try {
              const respuestasFormateadas = await convertBlockCForTestCanciones(blockCResponses);
              await testService.enviarRespuestasTestC(candidateId, respuestasFormateadas);
            } catch (error) {
            }
          }
          break;
          
        case 'D':
          const blockDAnswers = testState.responses.blockD;
          if (blockDAnswers && blockDAnswers.length > 0) {
            try {
              const { mostPreferred, secondPreferred } = convertBlockDForDrawingTest(blockDAnswers);
              await testService.enviarRespuestasTestD(candidateId, mostPreferred, secondPreferred);
            } catch (error) {
            }
          }
          break;
          
        default:
      }
    } catch (error) {
    }
  }, [testState.responses]);

  /**
   * Maneja las transiciones hacia atrás del Bloque C
   */
  const handleBlockCBackTransition = useCallback(async (currentQuestionIndex, saveResponse) => {
    const currentResponse = testState.responses.blockC[currentQuestionIndex] || {};
    const currentScreen = currentResponse.currentScreen || 'individual';
    const currentSongIndex = currentResponse.currentSongIndex || 0;
    const currentPairIndex = currentResponse.currentPairIndex || 0;
    
    try {
      const blockQuestions = await loadBlockQuestions('C');
      const question = blockQuestions[0]; // Solo hay una pregunta en el bloque C
      
      if (currentScreen === 'individual') {
        // Retroceder a la canción anterior
        const prevSongIndex = currentSongIndex - 1;
        if (prevSongIndex >= 0) {
          const newResponse = { 
            ...currentResponse, 
            currentSongIndex: prevSongIndex 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        }
        // Si es la primera canción, no se puede retroceder más dentro del bloque
        return false;
      } else if (currentScreen === 'comparison') {
        // Retroceder al par anterior o volver a la fase individual
        const prevPairIndex = currentPairIndex - 1;
        if (prevPairIndex >= 0) {
          const newResponse = { 
            ...currentResponse, 
            currentPairIndex: prevPairIndex 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        } else {
          // Volver a la última canción de la fase individual
          const newResponse = { 
            ...currentResponse, 
            currentScreen: 'individual', 
            currentSongIndex: question.songs.length - 1 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        }
      }
    } catch (error) {
    }
    return false;
  }, [testState.responses.blockC]);

  /**
   * Maneja las transiciones internas del Bloque C
   */
  const handleBlockCTransition = useCallback(async (currentQuestionIndex, saveResponse) => {
    const currentResponse = testState.responses.blockC[currentQuestionIndex] || {};
    const currentScreen = currentResponse.currentScreen || 'individual';
    const currentSongIndex = currentResponse.currentSongIndex || 0;
    const currentPairIndex = currentResponse.currentPairIndex || 0;
    
    try {
      const blockQuestions = await loadBlockQuestions('C');
      const question = blockQuestions[0]; // Solo hay una pregunta en el bloque C
      
      if (currentScreen === 'individual') {
        // Avanzar a la siguiente canción o ir a comparación si es la última
        const nextSongIndex = currentSongIndex + 1;
        if (nextSongIndex < question.songs.length) {
          const newResponse = { 
            ...currentResponse, 
            currentSongIndex: nextSongIndex 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        } else {
          // Ir a la fase de comparación
          const newResponse = { 
            ...currentResponse, 
            currentScreen: 'comparison', 
            currentPairIndex: 0 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        }
      } else if (currentScreen === 'comparison') {
        // Avanzar al siguiente par o finalizar si es el último
        const nextPairIndex = currentPairIndex + 1;
        if (nextPairIndex < question.pairs.length) {
          const newResponse = { 
            ...currentResponse, 
            currentPairIndex: nextPairIndex 
          };
          saveResponse('C', currentQuestionIndex, newResponse);
          return true;
        }
      }
    } catch (error) {
    }
    return false;
  }, [testState.responses.blockC]);

  /**
   * Navega a la siguiente pregunta o bloque
   */
  const goToNextQuestion = useCallback(async (saveResponse) => {
    const currentBlockKey = testState.currentBlock;
    const currentQuestionIndex = testState.currentQuestion;
    const currentBlockQuestions = await loadBlockQuestions(currentBlockKey);

    if (currentBlockKey === 'C' && await handleBlockCTransition(currentQuestionIndex, saveResponse)) {
      return;
    }

    if (currentQuestionIndex < currentBlockQuestions.length - 1) {
      navigateToQuestion(currentQuestionIndex + 1);
    } else {
      const blockOrder = ['A', 'B', 'C', 'D'];
      const currentBlockIndex = blockOrder.indexOf(currentBlockKey);
      
      if (currentBlockIndex < blockOrder.length - 1) {
        const candidateId = user?.id;
        if (candidateId) {
          downloadBlockResults(currentBlockKey, candidateId);
        }
        navigateToBlock(blockOrder[currentBlockIndex + 1], 0);
      } else {
        const candidateId = user?.id;
        if (candidateId) {
          downloadBlockResults(currentBlockKey, candidateId);
        }
        
        setTestState(prev => ({
          ...prev,
          endTime: new Date().toISOString()
        }));
        navigateToScreen('feedback');
      }
    }
  }, [testState.currentBlock, testState.currentQuestion, navigateToQuestion, navigateToBlock, navigateToScreen, downloadBlockResults, handleBlockCTransition, setTestState]);

  /**
   * Navega a la pregunta anterior
   */
  const goToPreviousQuestion = useCallback(async (saveResponse) => {
    const currentBlockKey = testState.currentBlock;
    const currentQuestionIndex = testState.currentQuestion;

    // Manejar retroceso especial para BlockC
    if (currentBlockKey === 'C' && await handleBlockCBackTransition(currentQuestionIndex, saveResponse)) {
      return;
    }

    if (currentQuestionIndex > 0) {
      navigateToQuestion(currentQuestionIndex - 1);
    } else {
      const blockOrder = ['A', 'B', 'C', 'D'];
      const currentBlockIndex = blockOrder.indexOf(currentBlockKey);
      
      if (currentBlockIndex > 0) {
        const previousBlockKey = blockOrder[currentBlockIndex - 1];
        const previousBlockQuestions = await loadBlockQuestions(previousBlockKey);
        navigateToBlock(previousBlockKey, previousBlockQuestions.length - 1);
      }
    }
  }, [testState.currentBlock, testState.currentQuestion, navigateToQuestion, navigateToBlock, handleBlockCBackTransition]);

  /**
   * Verifica si se puede navegar hacia atrás
   */
  const canGoPrevious = useCallback(() => {
    const currentBlockKey = testState.currentBlock;
    const currentQuestionIndex = testState.currentQuestion;
    const blockOrder = ['A', 'B', 'C', 'D'];
    const currentBlockIndex = blockOrder.indexOf(currentBlockKey);
    
    return currentQuestionIndex > 0 || currentBlockIndex > 0;
  }, [testState.currentBlock, testState.currentQuestion]);

  return {
    navigateToBlock,
    navigateToQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    canGoPrevious
  };
};

/**
 * Mapeo de palabras del frontend al formato esperado por el backend
 */
const wordMapping = {
  'Fuerza de voluntad': 'Fuerza Voluntad',
  'Confianza en sí mismo': 'Confianza Sí Mismo',
  'Mesurado para hablar': 'Mesurado Hablar'
};

/**
 * Convierte respuestas del Bloque A al formato requerido para el test Cleaver
 */
const convertBlockAForCleaver = (blockAResponses) => {
  const respuestas = [];
  
  blockAResponses.forEach((response) => {
    if (response && typeof response === 'object' && response.more && response.less) {
      // Mapear palabras al formato esperado por el backend
      const moreMapped = wordMapping[response.more] || response.more;
      const lessMapped = wordMapping[response.less] || response.less;
      
      // NO reemplazar espacios - el backend los espera con espacios normales
      respuestas.push(moreMapped, lessMapped);
    }
  });
  
  return respuestas;
};

/**
 * Convierte respuestas del Bloque B al formato requerido para testValoresService
 */
const convertBlockBForTestValores = (blockBResponses) => {
  const respuestasFormateadas = {};
  
  blockBResponses.forEach((response, index) => {
    if (response && typeof response === 'object' && Object.keys(response).length === 4) {
      const preguntaNum = index + 1;
      const rankings = [response[0], response[1], response[2], response[3]];
      const uniqueRankings = [...new Set(rankings)];
      
      if (uniqueRankings.length === 4 && uniqueRankings.every(r => r >= 1 && r <= 4)) {
        respuestasFormateadas[preguntaNum] = {
          A: response[0],
          B: response[1],
          C: response[2],
          D: response[3]
        };
      }
    }
  });
  
  return respuestasFormateadas;
};

/**
 * Convierte respuestas del Bloque C al formato requerido para testService (canciones)
 */
const convertBlockCForTestCanciones = async (blockCResponses) => {
  try {
    if (!blockCResponses || blockCResponses.length === 0) {
      throw new Error('No hay respuestas del Block C para convertir');
    }

    const blockCQuestions = await loadBlockQuestions('C');
    const blockCData = blockCQuestions[0];
    const response = blockCResponses[0];
    
    if (!response || typeof response !== 'object') {
      throw new Error('Respuesta del Block C inválida');
    }

    const respuestasFormateadas = {
      fase_1: {},
      fase_2: {}
    };

    const songNameMap = {
      'song1': 'No Diggity',
      'song2': 'Lose Yourself',
      'song3': 'My Sharona',
      'song4': 'Titanium'
    };

    // Fase 1: Respuestas individuales de canciones
    if (response.individual && typeof response.individual === 'object') {
      for (const [songId, rating] of Object.entries(response.individual)) {
        const songName = songNameMap[songId] || songId;
        const ratingString = rating === true ? "me gusta" : "no me gusta";
        respuestasFormateadas.fase_1[songName] = ratingString;
      }
    }

    // Fase 2: Respuestas de comparaciones
    if (response.comparison && typeof response.comparison === 'object') {
      for (const [pairId, selectedOption] of Object.entries(response.comparison)) {
        const pair = blockCData.pairs?.find(p => p.id === pairId);
        if (pair) {
          const songAName = songNameMap[pair.songA] || pair.songA;
          const songBName = songNameMap[pair.songB] || pair.songB;
          const comparisonKey = `${songAName} vs ${songBName}`;
          const selectedSong = selectedOption === 'A' ? songAName : songBName;
          respuestasFormateadas.fase_2[comparisonKey] = selectedSong;
        }
      }
    }

    return respuestasFormateadas;

  } catch (error) {
    throw { message: 'Error al convertir las respuestas del test C' };
  }
};

/**
 * Convierte respuestas del Bloque D al formato requerido para testService (dibujos)
 */
const convertBlockDForDrawingTest = (blockDResponses) => {
  try {
    if (!blockDResponses || blockDResponses.length === 0) {
      throw new Error('No hay respuestas del Block D para convertir');
    }

    const response = blockDResponses[0]; // Solo hay una pregunta en el bloque D
    
    if (!response || typeof response !== 'object') {
      throw new Error('Respuesta del Block D inválida');
    }

    // Buscar las opciones marcadas con valor 1 (más preferido) y 2 (segundo más preferido)
    let mostPreferred = null;
    let secondPreferred = null;

    for (const [optionIndex, value] of Object.entries(response)) {
      if (value === 1) {
        mostPreferred = optionIndex;
      } else if (value === 2) {
        secondPreferred = optionIndex;
      }
    }

    if (mostPreferred === null || secondPreferred === null) {
      throw new Error('Faltan respuestas requeridas: debe haber un dibujo más preferido (1) y un segundo más preferido (2)');
    }

    return {
      mostPreferred: mostPreferred.toString(),
      secondPreferred: secondPreferred.toString()
    };

  } catch (error) {
    throw { message: 'Error al convertir las respuestas del test D (dibujos)' };
  }
};

export default useTestNavigation;